"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import { DirectorySyncProviders, Directory } from "../types";
import LoadingContainer from "../../shared/LoadingContainer/index";
import type { DirectoryListProps, DirectoryType } from "../types";
import defaultClasses from "./index.module.css";
import {
  BadgeProps,
  PageToken,
  PaginatePayload,
  TableProps,
} from "../../shared/types";
import { sendHTTPRequest } from "../../shared/http";
import Paginate from "../../shared/Paginate/index";
import PaginatedTable from "../../shared/Table/paginated";
import NonPaginatedTable from "../../shared/Table/non-paginated";
const DEFAULT_VALUES = {
  directoryListData: [] as Directory[],
};

function DirectoryList(props: DirectoryListProps) {
  const [directoryListData, setDirectoryListData] = useState(
    () => DEFAULT_VALUES.directoryListData
  );

  function providers() {
    return Object.entries<string>(DirectorySyncProviders)?.map(
      ([value, text]) => ({
        value,
        text,
      })
    );
  }

  const [isDirectoryListLoading, setIsDirectoryListLoading] = useState(
    () => true
  );

  const [pageTokenMap, setPageTokenMap] = useState(() => ({}));

  const [showErrorComponent, setShowErrorComponent] = useState(() => false);

  const [errorMessage, setErrorMessage] = useState(() => "");

  function getUrl() {
    return props.urls.get;
  }

  function isPaginated() {
    return props.paginate?.itemsPerPage !== undefined;
  }

  function actions() {
    if (props.hideViewAction) {
      return [
        {
          icon: "PencilIcon",
          label: "Edit",
          handleClick: (directory: Directory) =>
            props.handleActionClick("edit", directory),
        },
      ];
    }
    return [
      {
        icon: "EyeIcon",
        label: "View",
        handleClick: (directory: Directory) =>
          props.handleActionClick("view", directory),
      },
      {
        icon: "PencilIcon",
        label: "Edit",
        handleClick: (directory: Directory) =>
          props.handleActionClick("edit", directory),
      },
    ];
  }

  function colsToDisplay() {
    return (
      props.cols || ["name", "tenant", "product", "type", "status", "actions"]
    ).map((_col) => {
      if (_col === "status") {
        return {
          name: "status",
          badge: {
            position: "surround",
            variantSelector(rowData) {
              let _variant: BadgeProps["variant"];
              if (rowData.deactivated) {
                _variant = "warning";
              }
              if (!rowData.deactivated) {
                _variant = "success";
              }
              return _variant;
            },
          },
        };
      } else {
        return _col;
      }
    }) as TableProps["cols"];
  }

  function listFetchUrl(
    params: Partial<PaginatePayload> &
      Pick<DirectoryListProps, "tenant" | "product"> & {
        getUrl: string;
      }
  ) {
    let _url = params.getUrl;
    const [urlPath, qs] = _url.split("?");
    const urlParams = new URLSearchParams(qs);
    if (params.tenant) {
      urlParams.set("tenant", params.tenant);
    }
    if (params.product) {
      urlParams.set("product", params.product);
    }
    if (params.pageToken) {
      urlParams.set("pageToken", params.pageToken);
    }
    if (params?.offset !== undefined) {
      urlParams.set("pageOffset", `${params.offset}`);
      urlParams.set("pageLimit", `${params.limit}`);
    }
    if (urlParams.toString()) {
      return `${urlPath}?${urlParams}`;
    }
    return _url;
  }

  function baseFetchUrl() {
    return listFetchUrl({
      getUrl: getUrl(),
      tenant: props.tenant,
      product: props.product,
    });
  }

  function tablePropsComputed() {
    return {
      ...props.tableProps,
      classNames: {
        ...props.tableProps?.classNames,
        iconSpan: defaultClasses.iconSpan,
      },
    };
  }

  function updateTokenMap(offset: number, token: PageToken) {
    return {
      ...pageTokenMap,
      [offset]: token,
    };
  }

  async function getFieldsData(directoryListUrl: string) {
    // fetch request for obtaining directory lists data
    const response = await sendHTTPRequest<
      | {
          data: Directory[];
        }
      | {
          data: {
            data: Directory[];
          };
          pageToken: PageToken;
        }
    >(directoryListUrl);
    setIsDirectoryListLoading(false);
    if (response && typeof response === "object") {
      if ("error" in response && response.error) {
        setShowErrorComponent(true);
        setErrorMessage(response.error.message);
        typeof props.errorCallback === "function" &&
          props.errorCallback(response.error.message);
      } else if ("data" in response) {
        const isTokenizedPagination = "pageToken" in response;
        const _data = isTokenizedPagination
          ? response.data.data
          : response.data;
        if (Array.isArray(_data)) {
          const directoriesListData = _data.map((directory: Directory) => {
            return {
              ...directory,
              id: directory.id,
              name: directory.name,
              tenant: directory.tenant,
              product: directory.product,
              type: providers().find(({ value }) => value === directory.type)
                ?.text as DirectoryType,
              status: directory.deactivated ? "Inactive" : "Active",
            };
          });
          setDirectoryListData(directoriesListData);
          typeof props.handleListFetchComplete === "function" &&
            props.handleListFetchComplete(directoriesListData);
        }
        if (isTokenizedPagination) {
          return response.pageToken;
        }
      }
    }
  }

  async function reFetch(payload: PaginatePayload) {
    const pageToken = await getFieldsData(
      listFetchUrl({
        getUrl: baseFetchUrl(),
        ...payload,
      })
    );
    if (pageToken) {
      setPageTokenMap(updateTokenMap(payload.offset, pageToken));
    }
  }

  useEffect(() => {
    if (!isPaginated()) {
      getFieldsData(baseFetchUrl());
    }
  }, [baseFetchUrl(), isPaginated()]);

  return (
    <LoadingContainer isBusy={isDirectoryListLoading}>
      {isPaginated() ? (
        <Paginate
          itemsPerPage={props.paginate!.itemsPerPage}
          currentPageItemsCount={directoryListData.length}
          handlePageChange={props.paginate?.handlePageChange}
          reFetch={reFetch}
          pageTokenMap={pageTokenMap}
        >
          <PaginatedTable
            emptyStateMessage="No directories found."
            cols={colsToDisplay()}
            data={directoryListData}
            actions={actions()}
            showErrorComponent={showErrorComponent}
            errorMessage={errorMessage}
            tableProps={tablePropsComputed()}
          />
        </Paginate>
      ) : null}
      {!isPaginated() ? (
        <NonPaginatedTable
          emptyStateMessage="No directories found."
          cols={colsToDisplay()}
          data={directoryListData}
          actions={actions()}
          showErrorComponent={showErrorComponent}
          errorMessage={errorMessage}
          tableProps={tablePropsComputed()}
        />
      ) : null}
    </LoadingContainer>
  );
}

export default DirectoryList;
